import { ArrowLeft } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { cn } from "./cn";

/**
 * Encabezado de página — el único `<h1>` de cada pantalla (DESIGN.md §5).
 *
 * Convención de "volver": quien navega a una pantalla de detalle puede
 * dejar el origen en `location.state`:
 *
 *   navigate(`/socios/${id}/cuenta-corriente`, {
 *     state: { desde: { etiqueta: "Morosidad", ruta: "/morosidad?tramo=90" } },
 *   });
 *
 * y el link dice "Volver a Morosidad" y vuelve con los filtros. Sin
 * `state.desde`, usa `volver` (ruta y etiqueta por defecto de la pantalla).
 */
export interface OrigenNavegacion {
  /** Nombre de la pantalla de origen, tal como aparece en el menú. */
  etiqueta: string;
  /** Ruta completa, con query string si traía filtros. */
  ruta: string;
}

/** Lee `location.state.desde` validando la forma (el state es `unknown`). */
export function leerOrigen(state: unknown): OrigenNavegacion | null {
  if (typeof state !== "object" || state === null || !("desde" in state)) return null;
  const desde = (state as { desde: unknown }).desde;
  if (typeof desde !== "object" || desde === null) return null;
  const { etiqueta, ruta } = desde as Record<string, unknown>;
  if (typeof etiqueta !== "string" || typeof ruta !== "string") return null;
  // Sólo rutas internas: el state no debe poder mandar a otro origen
  // ("//host" es una URL relativa al protocolo).
  if (!ruta.startsWith("/") || ruta.startsWith("//")) return null;
  return { etiqueta, ruta };
}

export interface PageHeaderProps {
  /** Texto del `<h1>`. Debe coincidir con el nombre del ítem del menú. */
  title: React.ReactNode;
  /** Bajada opcional: qué se hace en esta pantalla, en una línea. */
  subtitle?: React.ReactNode | undefined;
  /** Ícono lucide decorativo a la izquierda del título. */
  icon?: LucideIcon | undefined;
  /** Botones a la derecha (hacen wrap bajo el título en móvil). */
  actions?: React.ReactNode | undefined;
  /** Link "Volver" por defecto. `location.state.desde` lo reemplaza. */
  volver?: OrigenNavegacion | undefined;
  className?: string | undefined;
}

export function PageHeader({
  title,
  subtitle,
  icon: Icon,
  actions,
  volver,
  className,
}: PageHeaderProps): JSX.Element {
  const location = useLocation();
  const origen = leerOrigen(location.state) ?? volver ?? null;

  return (
    <header className={cn("space-y-2", className)}>
      {origen && (
        <Link
          to={origen.ruta}
          className="inline-flex items-center gap-1 rounded text-sm text-brand-700 hover:text-brand-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Volver a {origen.etiqueta}
        </Link>
      )}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 items-start gap-2">
          {Icon && (
            <Icon
              className="mt-1 h-6 w-6 shrink-0 text-neutral-500"
              strokeWidth={1.5}
              aria-hidden="true"
            />
          )}
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-neutral-900">{title}</h1>
            {subtitle && <p className="mt-1 text-sm text-neutral-600">{subtitle}</p>}
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}
